import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Search, SlidersHorizontal, X, BookOpen, FileText, Calendar,
  Globe, Tag, ChevronLeft, ChevronRight, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Navigation from "../components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Active filter chip ── */
const Chip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-50 text-orange-700 text-xs rounded-full border border-orange-200">
    {label}
    <button onClick={onRemove} className="hover:text-orange-900"><X className="w-3 h-3" /></button>
  </span>
);

/* ── Filter select ── */
const FilterSelect = ({ label, value, onChange, options, placeholder, testId }) => (
  <div>
    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
      data-testid={testId}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </div>
);

/* ── Range input pair ── */
const RangeInput = ({ label, fromVal, toVal, onFromChange, onToChange, placeholder, type = "number", testIdPrefix }) => (
  <div>
    <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">{label}</label>
    <div className="flex gap-2">
      <Input
        type={type}
        placeholder={`Min`}
        value={fromVal}
        onChange={(e) => onFromChange(e.target.value)}
        className="text-sm"
        data-testid={`${testIdPrefix}-from`}
      />
      <Input
        type={type}
        placeholder={`Max`}
        value={toVal}
        onChange={(e) => onToChange(e.target.value)}
        className="text-sm"
        data-testid={`${testIdPrefix}-to`}
      />
    </div>
  </div>
);

const SmartSearchPage = ({ user }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  /* State */
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(parseInt(searchParams.get("page")) || 1);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [availableFilters, setAvailableFilters] = useState({
    languages: [], categories: [], file_types: [], organizations: [],
  });

  const [filters, setFilters] = useState({
    language: searchParams.get("language") || "",
    category: searchParams.get("category") || "",
    file_type: searchParams.get("file_type") || "",
    organization: searchParams.get("organization") || "",
    authors: searchParams.get("authors") || "",
    tags: searchParams.get("tags") || "",
    year_from: searchParams.get("year_from") || "",
    year_to: searchParams.get("year_to") || "",
    price_min: searchParams.get("price_min") || "",
    price_max: searchParams.get("price_max") || "",
    has_file: searchParams.get("has_file") || "",
    sort_by: searchParams.get("sort_by") || "relevance",
    sort_order: searchParams.get("sort_order") || "desc",
  });

  const PER_PAGE = 20;

  /* ── Scroll tracking & restoration ── */
  const pendingScrollRef = useRef(null);
  const scrollYRef = useRef(0);

  // Restore scroll position and filtersOpen state on mount
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("smart_search_ui_state");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed.filtersOpen === "boolean") setFiltersOpen(parsed.filtersOpen);
        if (typeof parsed.scrollY === "number" && parsed.scrollY > 0) {
          pendingScrollRef.current = parsed.scrollY;
        }
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Execute deferred scroll restoration after results render
  useEffect(() => {
    if (pendingScrollRef.current != null && !loading && results.length > 0) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: pendingScrollRef.current, behavior: "auto" });
        pendingScrollRef.current = null;
      });
    }
  }, [loading, results]);

  // Track scroll position continuously
  useEffect(() => {
    const onScroll = () => { scrollYRef.current = window.scrollY; };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Persist UI state when leaving the page
  useEffect(() => {
    return () => {
      try {
        sessionStorage.setItem("smart_search_ui_state", JSON.stringify({
          filtersOpen,
          scrollY: scrollYRef.current,
        }));
      } catch (e) { /* ignore */ }
    };
  }, [filtersOpen]);

  /* ── Perform search ── */
  const performSearch = useCallback(async (overridePage) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("q", query.trim());
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== "all") params.set(k, v);
      });
      const p = overridePage || page;
      params.set("page", p);
      params.set("per_page", PER_PAGE);

      const resp = await axios.get(`${API}/smart-search?${params.toString()}`);
      setResults(resp.data.items || []);
      setTotal(resp.data.total || 0);
      if (resp.data.filters_available) setAvailableFilters(resp.data.filters_available);

      // Sync URL
      setSearchParams(params, { replace: true });
    } catch (err) {
      console.error("Smart search error:", err);
    } finally {
      setLoading(false);
    }
  }, [query, filters, page, setSearchParams]);

  /* Initial load */
  useEffect(() => { performSearch(); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    performSearch(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    performSearch(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const applyAndSearch = () => {
    setPage(1);
    performSearch(1);
  };

  const clearAllFilters = () => {
    setFilters({
      language: "", category: "", file_type: "", organization: "",
      authors: "", tags: "", year_from: "", year_to: "",
      price_min: "", price_max: "", has_file: "",
      sort_by: "relevance", sort_order: "desc",
    });
    setPage(1);
    setTimeout(() => performSearch(1), 0);
  };

  const removeFilter = (key) => {
    const updated = { ...filters, [key]: "" };
    setFilters(updated);
    setPage(1);
    // Trigger search with updated filters
    setLoading(true);
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    Object.entries(updated).forEach(([k, v]) => { if (v && v !== "all") params.set(k, v); });
    params.set("page", "1");
    params.set("per_page", PER_PAGE);
    axios.get(`${API}/smart-search?${params.toString()}`).then((resp) => {
      setResults(resp.data.items || []);
      setTotal(resp.data.total || 0);
      if (resp.data.filters_available) setAvailableFilters(resp.data.filters_available);
      setSearchParams(params, { replace: true });
    }).finally(() => setLoading(false));
  };

  /* Active filter chips */
  const activeFilters = Object.entries(filters)
    .filter(([k, v]) => v && !["sort_by", "sort_order"].includes(k))
    .map(([k, v]) => ({ key: k, label: `${k.replace(/_/g, " ")}: ${v}` }));

  const totalPages = Math.ceil(total / PER_PAGE);

  return (
    <div className="min-h-screen bg-sky-50">
      <Navigation user={user} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }} data-testid="smart-search-title">
            Smart Search
          </h1>
          <p className="text-sm text-slate-500 mt-1">Search across titles, descriptions, and file content with advanced filters</p>
        </div>

        <section className="mb-6 max-w-4xl text-slate-600 text-sm leading-relaxed" data-testid="smart-search-intro">
          <p className="mb-3">
            Combine full-text search inside every PDF with fine-grained filters — by <strong>language</strong>, <strong>category</strong> (article, book, thesis, report), <strong>publication year</strong>, <strong>author</strong>, <strong>organization</strong>, <strong>file type</strong>, and <strong>tags</strong>.
            Ideal for research in extractive metallurgy, mining engineering, hydrometallurgy, cobalt and copper processing, materials chemistry, and applied sciences.
            Results are ranked by relevance to your keywords across abstracts, titles, tags, and the full document text.
          </p>
          <p className="mb-3">
            Smart Search is designed for the way researchers actually work. Filter by <strong>year range</strong> to isolate historic references or the most recent literature; filter by <strong>language</strong> to focus on French, English, Spanish, Portuguese, or any other of our supported languages; filter by <strong>organization</strong> to see everything published by a specific university, research center, or industrial group; and combine any number of tag filters to zero in on cross-disciplinary topics.
            Once you find useful results, save them into a <strong>Reading Collection</strong> for later reference or share the collection with colleagues.
          </p>
          <p className="text-slate-500 text-xs">
            <strong>Frequent searches include:</strong> heap leaching kinetics · copper solvent extraction (SX-EW) · cobalt hydrometallurgy · pyrite oxidation · gold cyanidation · flotation reagents · tailings management · acid mine drainage · autoclave leaching · nickel laterite processing · zinc electrolysis · lithium extraction from brines · rare-earth separation · sulfide roasting · slag chemistry · industrial safety in mining.
          </p>
        </section>

        {/* Trending topic chips — click to pre-fill search + boost SEO with internal links */}
        <div className="mb-6" data-testid="smart-search-trending">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">
            Trending topics
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              "cobalt hydrometallurgy",
              "copper SX-EW",
              "gold cyanidation",
              "flotation reagents",
              "heap leaching",
              "tailings management",
              "acid mine drainage",
              "nickel laterite",
              "lithium brines",
              "rare-earth separation",
              "zinc electrolysis",
              "sulfide roasting",
              "mine ventilation",
              "rock mechanics",
              "corrosion",
              "battery materials",
            ].map((topic) => (
              <button
                key={topic}
                type="button"
                onClick={() => {
                  setQuery(topic);
                  // Fire the search immediately so users see instant results
                  setTimeout(() => {
                    const form = document.querySelector('form');
                    if (form) form.requestSubmit();
                  }, 0);
                }}
                className="px-3 py-1.5 text-xs rounded-full border border-slate-300 bg-white text-slate-700 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700 transition-colors"
                data-testid={`trending-${topic.replace(/\s+/g, '-')}`}
              >
                {topic}
              </button>
            ))}
          </div>
        </div>

        {/* Search bar */}
        <form onSubmit={handleSubmit} className="mb-6">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search titles, abstracts, authors, file content..."
                className="w-full pl-12 pr-4 h-12 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                data-testid="smart-search-input"
              />
            </div>
            <Button type="submit" className="h-12 px-6 bg-orange-500 hover:bg-orange-600 text-white" data-testid="smart-search-submit">
              Search
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-12 px-4 lg:hidden"
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              <SlidersHorizontal className="w-4 h-4" />
            </Button>
          </div>
        </form>

        {/* Active filter chips */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="active-filters">
            {activeFilters.map(({ key, label }) => (
              <Chip key={key} label={label} onRemove={() => removeFilter(key)} />
            ))}
            <button onClick={clearAllFilters} className="text-xs text-slate-500 hover:text-slate-700 underline">
              Clear all
            </button>
          </div>
        )}

        {/* Main layout: sidebar + results */}
        <div className="flex gap-6">
          {/* Filter sidebar */}
          <aside
            className={`shrink-0 w-64 ${filtersOpen ? "block" : "hidden"} lg:block`}
            data-testid="filter-sidebar"
          >
            <div className="bg-white border border-slate-200 rounded-lg p-5 sticky top-24 space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Filters</h3>
                <button onClick={clearAllFilters} className="text-xs text-slate-500 hover:text-orange-600" data-testid="clear-all-filters">
                  Reset
                </button>
              </div>

              <FilterSelect
                label="Language" value={filters.language}
                onChange={(v) => applyFilter("language", v)}
                options={availableFilters.languages} placeholder="All languages"
                testId="filter-language"
              />
              <FilterSelect
                label="Category" value={filters.category}
                onChange={(v) => applyFilter("category", v)}
                options={availableFilters.categories} placeholder="All categories"
                testId="filter-category"
              />
              <FilterSelect
                label="File type" value={filters.file_type}
                onChange={(v) => applyFilter("file_type", v)}
                options={availableFilters.file_types} placeholder="All types"
                testId="filter-file-type"
              />
              <FilterSelect
                label="Organization" value={filters.organization}
                onChange={(v) => applyFilter("organization", v)}
                options={availableFilters.organizations} placeholder="All orgs"
                testId="filter-organization"
              />

              <RangeInput
                label="Year range"
                fromVal={filters.year_from} toVal={filters.year_to}
                onFromChange={(v) => applyFilter("year_from", v)}
                onToChange={(v) => applyFilter("year_to", v)}
                testIdPrefix="filter-year"
              />
              <RangeInput
                label="Price range"
                fromVal={filters.price_min} toVal={filters.price_max}
                onFromChange={(v) => applyFilter("price_min", v)}
                onToChange={(v) => applyFilter("price_max", v)}
                testIdPrefix="filter-price"
              />

              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Authors</label>
                <Input
                  type="text"
                  placeholder="e.g., Dr. Smith"
                  value={filters.authors}
                  onChange={(e) => applyFilter("authors", e.target.value)}
                  className="text-sm"
                  data-testid="filter-authors"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Tags</label>
                <Input
                  type="text"
                  placeholder="e.g., Research, AI"
                  value={filters.tags}
                  onChange={(e) => applyFilter("tags", e.target.value)}
                  className="text-sm"
                  data-testid="filter-tags"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="hasFile"
                  checked={filters.has_file === "true"}
                  onChange={(e) => applyFilter("has_file", e.target.checked ? "true" : "")}
                  className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-400"
                  data-testid="filter-has-file"
                />
                <label htmlFor="hasFile" className="text-sm text-slate-700">Has downloadable file</label>
              </div>

              <Button onClick={applyAndSearch} className="w-full bg-orange-500 hover:bg-orange-600 text-white" data-testid="apply-filters-btn">
                Apply Filters
              </Button>
            </div>
          </aside>

          {/* Results area */}
          <div className="flex-1 min-w-0">
            {/* Results header */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-600" data-testid="results-count">
                {loading ? "Searching..." : `${total} result${total !== 1 ? "s" : ""} found`}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Sort:</span>
                <select
                  value={filters.sort_by}
                  onChange={(e) => { applyFilter("sort_by", e.target.value); }}
                  className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
                  data-testid="sort-select"
                >
                  <option value="relevance">Relevance</option>
                  <option value="created_at">Date added</option>
                  <option value="title">Title</option>
                  <option value="year">Year</option>
                  <option value="price">Price</option>
                </select>
                <button
                  onClick={() => applyFilter("sort_order", filters.sort_order === "asc" ? "desc" : "asc")}
                  className="text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded px-2 py-1"
                  data-testid="sort-order-toggle"
                >
                  {filters.sort_order === "asc" ? "A-Z" : "Z-A"}
                </button>
              </div>
            </div>

            {/* Results list */}
            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
              </div>
            ) : results.length === 0 ? (
              <div className="text-center py-16">
                <BookOpen className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500 text-lg">No results found</p>
                <p className="text-slate-400 text-sm mt-1">Try different keywords or adjust your filters</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="search-results">
                {results.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white border border-slate-200 rounded-lg p-5 hover:border-orange-300 hover:shadow-sm transition-all cursor-pointer"
                    onClick={() => navigate(`/items/${item.id}`)}
                    data-testid={`result-${item.id}`}
                  >
                    <div className="flex gap-4">
                      {/* Thumbnail */}
                      {item.has_file && item.file_type === "PDF" ? (
                        <div className="shrink-0 w-[80px] h-[106px] bg-slate-50 rounded overflow-hidden border border-slate-100">
                          <img
                            src={`${API}/items/${item.id}/thumbnail`}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        </div>
                      ) : item.has_file ? (
                        <div className="shrink-0 w-[80px] h-[106px] bg-slate-50 rounded border border-slate-100 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-slate-300" />
                        </div>
                      ) : null}

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-base font-semibold text-slate-900 line-clamp-1" style={{ fontFamily: "Fraunces, serif" }}>
                            {item.title}
                          </h3>
                          {item.price != null && item.price > 0 && (
                            <span className="shrink-0 text-sm font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded">
                              ${item.price.toFixed(2)}
                            </span>
                          )}
                        </div>

                        {item.authors?.length > 0 && (
                          <p className="text-sm text-slate-500 mt-0.5">{item.authors.join(", ")}</p>
                        )}

                        {item.abstract && (
                          <p className="text-sm text-slate-600 line-clamp-2 mt-1">{item.abstract}</p>
                        )}

                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {item.year && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                              <Calendar className="w-3 h-3" />{item.year}
                            </span>
                          )}
                          {item.language && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                              <Globe className="w-3 h-3" />{item.language}
                            </span>
                          )}
                          {item.file_type && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                              <FileText className="w-3 h-3" />{item.file_type}
                            </span>
                          )}
                          {item.category && (
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded capitalize">
                              {item.category}
                            </span>
                          )}
                          {item.tags?.slice(0, 3).map((tag) => (
                            <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-orange-600 text-xs rounded">
                              <Tag className="w-3 h-3" />{tag}
                            </span>
                          ))}
                          {item.tags?.length > 3 && (
                            <span className="text-xs text-slate-400">+{item.tags.length - 3}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8" data-testid="pagination">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => handlePageChange(page - 1)}
                  data-testid="page-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let p;
                  if (totalPages <= 7) {
                    p = i + 1;
                  } else if (page <= 4) {
                    p = i + 1;
                  } else if (page >= totalPages - 3) {
                    p = totalPages - 6 + i;
                  } else {
                    p = page - 3 + i;
                  }
                  return (
                    <Button
                      key={p}
                      variant={p === page ? "default" : "outline"}
                      size="sm"
                      className={p === page ? "bg-orange-500 hover:bg-orange-600 text-white" : ""}
                      onClick={() => handlePageChange(p)}
                      data-testid={`page-${p}`}
                    >
                      {p}
                    </Button>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => handlePageChange(page + 1)}
                  data-testid="page-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartSearchPage;
